import type { AxiosError, AxiosResponse } from 'axios';
import type { CoreApiError } from '@lqa-frontend/api-services';
import type {
	UseMutationOptions,
	UseQueryOptions,
} from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	coreApiClient,
	coreApiQueryKey,
	endpoints,
} from '@lqa-frontend/api-services';

/**
 * Response shape for `GET /user-preference/view-config/{viewKey}`.
 *
 * The backend echoes the `viewKey` and returns the saved-or-default ordered
 * column list under `lockedColumns`. Order encodes display order; presence
 * encodes visibility (anything absent is hidden).
 */
export interface ViewConfigResponse {
	viewKey: string;
	lockedColumns: string[];
}

export type ViewConfigQueryOptions = Omit<
	UseQueryOptions<
		AxiosResponse<ViewConfigResponse>,
		AxiosError<CoreApiError>,
		string[]
	>,
	'queryKey' | 'queryFn' | 'select' | 'enabled'
>;

/**
 * Load the saved column ordering for a given view. Returns the default
 * config when no user-saved config exists (handled by the backend).
 *
 * Pass `null` for `viewKey` to disable the query.
 */
export function useViewConfigQuery(
	viewKey: string | null,
	options?: ViewConfigQueryOptions
) {
	return useQuery<
		AxiosResponse<ViewConfigResponse>,
		AxiosError<CoreApiError>,
		string[]
	>({
		queryKey: coreApiQueryKey(
			endpoints.userPreference.viewConfig(viewKey ?? ''),
			'get'
		),
		queryFn: async () =>
			coreApiClient.get<ViewConfigResponse>(
				endpoints.userPreference.viewConfig(viewKey as string)
			),
		enabled: viewKey != null,
		...options,
		select: (response) => response.data.lockedColumns,
	});
}

/**
 * Body for `PUT /user-preference/view-config/{viewKey}`. The backend stores
 * an ordered list of *visible* column ids — order encodes display order;
 * absence encodes hidden.
 */
export interface ViewConfigPayload {
	lockedColumns: string[];
}

export interface UpdateViewConfigVariables {
	viewKey: string;
	lockedColumns: string[];
}

/**
 * Persist a column ordering for a given view. On success, invalidates the
 * matching GET so subsequent reads see the new state.
 */
export function useUpdateViewConfigMutation(
	options?: UseMutationOptions<
		AxiosResponse<ViewConfigPayload>,
		AxiosError<CoreApiError>,
		UpdateViewConfigVariables
	>
) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ viewKey, lockedColumns }: UpdateViewConfigVariables) =>
			coreApiClient.put<ViewConfigPayload>(
				endpoints.userPreference.viewConfig(viewKey),
				{ lockedColumns }
			),
		...options,
		onSuccess: async (...args) => {
			const [, variables] = args;
			await queryClient.invalidateQueries({
				queryKey: coreApiQueryKey(
					endpoints.userPreference.viewConfig(variables.viewKey),
					'get'
				),
			});
			options?.onSuccess?.(...args);
		},
	});
}
